import { LayoutConfig } from '../../../models/user-config/DisplayModeConfig';
import UniqueId from '../../uid/UniqueId';
import DatasetsLayoutNode from '../dataset-tree/layout/DatasetsLayoutNode';
import DatasetTreeNode from '../dataset-tree/logical/DatasetTreeNode';
import DatasetLayoutViewModel from '../view-model/DatasetLayoutViewModel';
import DatasetViewModel from '../view-model/DatasetViewModel';

import DatasetTitlesTranslator from './DatasetTitlesTranslator';
import ViewModelCompositorLayoutVisitor from './ViewModelCompositorLayoutVisitor';

/**
 * Creates view models of layout elements ({@link DatasetLayoutViewModel} and
 * datasets ({@link DatasetViewModel}) given a tree of layout elements
 * ({@link DatasetsLayoutNode}) created by a {@link IDatasetLayoutStrategy}
 * from a logical tree of datasets ({@link DatasetTreeNode}).
 */
class ViewModelCompositor {
  private datasets: Record<UniqueId, DatasetViewModel> = {};

  private layout: Record<UniqueId, DatasetLayoutViewModel> = {};

  private layoutRoot: UniqueId | null = null;

  private constructedDatasetsCache: Map<DatasetTreeNode, DatasetViewModel> =
    new Map();

  constructor(
    private tree: DatasetsLayoutNode,
    private datasetTitlesTranslator: DatasetTitlesTranslator,
    private layoutConfig?: LayoutConfig
  ) {}

  /**
   * Composes the datasets layout tree the instance was configured with into
   * final view model structures.
   */
  public compose(): void {
    this.layoutRoot = this.addNode(this.tree);
  }

  /**
   * Returns the composed {@link DatasetViewModel} dictionary.
   */
  public getDatasets(): Record<UniqueId, DatasetViewModel> {
    return this.datasets;
  }

  /**
   * Returns the composed {@link DatasetLayoutViewModel} dictionary.
   */
  public getLayout(): Record<UniqueId, DatasetLayoutViewModel> {
    return this.layout;
  }

  /**
   * Returns the ID of the root layout element.
   */
  public getLayoutRoot(): UniqueId {
    if (!this.layoutRoot) {
      throw new Error('layout compositor: no root has been defined');
    }

    return this.layoutRoot;
  }

  /**
   * Processes a new layout tree node and returns the ID of a newly created
   * layout element view model.
   *
   * @param node - Layout tree node.
   */
  private addNode(node: DatasetsLayoutNode): UniqueId {
    const childIds: UniqueId[] = [];

    for (const child of node.getChildren()) {
      const id = this.addNode(child);
      childIds.push(id);
    }

    let nodeId: UniqueId | null = null;

    const visitor = new ViewModelCompositorLayoutVisitor(
      childIds,
      {
        addDatasetViewModel: (originalNode, dataset) =>
          this.addDataset(originalNode, dataset),

        addLayoutViewModel: layout => {
          this.addLayoutEntry(layout);
          nodeId = layout.id;
        },

        getConstructedDatasetViewModelId: originalNode =>
          this.getConstructedDatasetViewModelId(originalNode),

        getDatasetTitle: datasetNode =>
          this.datasetTitlesTranslator.getDatasetTitle(datasetNode),
      },
      this.layoutConfig
    );

    node.acceptVisitor(visitor);

    if (nodeId === null) {
      throw new Error('Layout visitor did not create a view model');
    }

    return nodeId;
  }

  /**
   * Adds a newly created dataset view model into the dictionary and remembers
   * its association with the original node in the logical dataset tree.
   *
   * @param node - A node in the dataset tree.
   * @param dataset - A view model of `node`.
   */
  private addDataset(node: DatasetTreeNode, dataset: DatasetViewModel) {
    this.constructedDatasetsCache.set(node, dataset);
    this.datasets[dataset.id] = dataset;
  }

  /**
   * Adds a newly created layout element view model into the dictionary.
   *
   * @param layoutEntry - A layout element representation.
   */
  private addLayoutEntry(layoutEntry: DatasetLayoutViewModel) {
    this.layout[layoutEntry.id] = layoutEntry;
  }

  /**
   * Returns the ID of a previously constructed {@link DatasetViewModel} object
   * for a given original {@link DatasetTreeNode} object, or null if it hasn't
   * been constructed yet.
   *
   * @param node - Original dataset tree node to query for.
   */
  private getConstructedDatasetViewModelId(
    node: DatasetTreeNode
  ): UniqueId | null {
    const viewModel = this.constructedDatasetsCache.get(node);

    if (viewModel) {
      return viewModel.id;
    }

    return null;
  }
}

export default ViewModelCompositor;
